import type { IconName } from '@hotjar/hotsauce';
import { Checkbox, Icon, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Device } from '../../../../domain/common/Responses/Device';
import { StyledFieldList } from '../../StyledFieldList';

const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

const LabelContainer = styled.div`
  display: inline-block;
  align-items: center;
`;

const DeviceCheckbox = ({
  selectedDevices,
  device,
  icon,
  label,
  onChange,
}: {
  selectedDevices: Device[];
  device: Device;
  icon: IconName;
  label: string;
  onChange: (device: Device) => void;
}) => {
  return (
    <Checkbox
      isChecked={selectedDevices.includes(device)}
      onChange={() => onChange(device)}
      labelContent={
        <LabelContainer>
          <StyledIcon icon={icon} />
          {label}
        </LabelContainer>
      }
    />
  );
};

export interface DeviceSelectorProps {
  devices: Device[];
  onDevicesChanged: (devices: Device[]) => void;
  context: 'survey' | 'widget' | 'invitation';
}

export const DeviceSelector = ({
  devices,
  onDevicesChanged,
  context,
}: DeviceSelectorProps): JSX.Element => {
  const { t } = useTranslation('feedback');

  const onToggle = (device: Device) => {
    const newDevices = devices.includes(device)
      ? devices.filter((d) => d !== device)
      : [...devices, device];

    onDevicesChanged(newDevices);
  };

  const isInvalid = devices.length === 0;
  const invalidText = t('wizard.device-selector.error-no-selection');

  return (
    <StyledFieldList
      size="l"
      isRequired
      isInvalid={isInvalid}
      invalidText={isInvalid ? invalidText : undefined}
      title={t('wizard.device-selector.title')}
      helperText={t('wizard.device-selector.helper-text', { entityType: context })}
    >
      <DeviceCheckbox
        label={t('wizard.device-selector.desktop')}
        selectedDevices={devices}
        device="desktop"
        icon="device_desktop"
        onChange={onToggle}
      />
      <DeviceCheckbox
        label={t('wizard.device-selector.tablet')}
        selectedDevices={devices}
        device="tablet"
        icon="device_tablet"
        onChange={onToggle}
      />
      <DeviceCheckbox
        label={t('wizard.device-selector.mobile')}
        selectedDevices={devices}
        device="phone"
        icon="device_phone"
        onChange={onToggle}
      />
    </StyledFieldList>
  );
};
